import { useGetBookMarksQuery } from '@intigral/domain/lib/presentation/hooks/useGetBookMarksQuery';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import AddWatchlistButton from '../../../components/elements/AddWatchlistButton';
import DetailsButton from '../../../components/elements/DetailsButton';
import ReminderButton from '../../../components/elements/ReminderButton';
import { ButtonTypes, COMPONENT_TYPES } from '../../../config/constants';
import channelsUtil from '../../../helpers/channelsUtil';
import h, { getCountry } from '../../../helpers/common';
import { checkShowLockIcon } from '../../../helpers/vod';
import { useConfigurationSections, useCountryCode } from '../../../hooks/useConfiguration';
import { usePPVEvents } from '../../../hooks/usePPVEvents';
import { IStore } from '../../../interfaces/StoreTypes';
import { handlePromotions } from '../../../managers/dataLoaderHandler';
import { getUserInfo } from '../../../utils/user';
import { LockButton, PlayButton } from '../components/elements';
import { PROMOTIONS_QUERY_KEY } from '../constants';
import { showDetailsButton, showPlayVideoButton, showReminderButton, showWatchlistButton } from '../utils/displayRules';
import { getMobileImages, processPromotions, savePromotionsToCache } from '../utils/promotions';
import user from '../../../helpers/user';

export const usePromotionUtils = ({ name }: { name: string }) => {
    const { channels } = useSelector((state: IStore) => state.liveTv.userChannels) ?? [];
    const { paidVsFree = {}, promotionProviderImages = {}, config } = useConfigurationSections();

    const enableLockIcon = (paidVsFree as any)?.lockIconEnable || false;

    const { data: bookmarkedItems } = useGetBookMarksQuery({
        apiKey: getUserInfo('apiKey'),
        profileGuid: user.getProfile().profileGuid,
        authToken: getUserInfo('authToken'),
        identityToken: getUserInfo('identityToken'),
        country: getCountry(),
        device: h.getDevice(),
        programType: 'all',
    });

    const getPromoButtons = (promotion) => {
        const isPPV = promotion.promoType === 'PPV';
        const subGuid = promotion?.subscriptionGuids;
        let isLocked = isPPV && subGuid?.length > 0 && checkShowLockIcon(subGuid ?? []) && enableLockIcon;

        let bookmarkedItem = null;
        bookmarkedItem = _.find(bookmarkedItems, (a) => a.paId === promotion.paId);

        if (promotion?.promoType === 'CATCHUP') {
            const filteredChannel = channelsUtil.getChannelByStationId(promotion.stationId, channels) || null;
            const guids = filteredChannel?.subscriptions.map((el) => el?.subscriptionGUID) || [];
            isLocked = guids.length > 0 && checkShowLockIcon(guids) && enableLockIcon;
            promotion = {
                ...promotion,
                channel: filteredChannel,
            };
        }

        const play =
            !isLocked && showPlayVideoButton(promotion) ? (
                <PlayButton key="play" bookmarkedItem={bookmarkedItem} promotion={promotion} name={name} />
            ) : undefined;

        const reminder = showReminderButton(promotion) ? (
            <ReminderButton key="reminder" buttonType={ButtonTypes.PPV} item={promotion} />
        ) : undefined;

        const watchlist = showWatchlistButton(promotion) ? (
            <AddWatchlistButton
                key="watchlist"
                item={promotion}
                type={ButtonTypes[name?.toLowerCase().includes('vertical') ? 'VodeItem' : 'VodFeaturedItem']}
            />
        ) : undefined;

        const details =
            !isLocked && showDetailsButton(promotion) ? (
                <DetailsButton
                    key="details"
                    buttonType={ButtonTypes.VodFeaturedItem}
                    item={promotion}
                    componentType={COMPONENT_TYPES.FEATURED_SLIDER}
                    componentName={name}
                    meta={{}}
                >
                    {/* &nbsp; */}
                </DetailsButton>
            ) : undefined;

        const buttons = [play, isLocked && <LockButton key="lock" />, reminder, watchlist, details].filter(Boolean);

        return { buttonsArray: buttons, buttonsObject: { play, isLocked, reminder, watchlist, details } };
    };

    const getPromoImages = (promotion) => {
        return {
            smallThumbnail: h.getImagizerUrl(
                promotion.images?.[0]?.url,
                config?.imagizer.components_images.TV_GUIDE_CAROUSEL_CHANNEL_LOGO
            ),
            thumbnail: h.getImagizerUrl(
                promotion.images?.[0]?.url,
                config?.imagizer.components_images.FEATURED_ITEM_MOBILE
            ),
            465: h.getImagizerUrl(getMobileImages(promotion), config?.imagizer?.components_images.FEATURED_ITEM_MOBILE),
            924: h.getImagizerUrl(promotion.image, config?.imagizer?.components_images.FEATURED_ITEM_DESKTOP),
            1200: h.getImagizerUrl(promotion.image, config?.imagizer?.components_images.FEATURED_ITEM_DESKTOP),
        };
    };

    const getProviderLogo = (promotion) => {
        return (
            h.getImagizerUrl(
                promotionProviderImages?.[promotion.categories]?.imageUrl,
                config?.imagizer.components_images.FEATURED_ITEM_MOBILE
            ) || ''
        );
    };

    return { getPromoButtons, getPromoImages, getProviderLogo };
};

export const usePromotions = (carousel) => {
    const queryClient = useQueryClient();
    const { channels } = useSelector((state: IStore) => state.liveTv.userChannels) ?? [];
    //  FIXED:  Using React Query-style hook instead of Redux generalConfiguration
    const { data: ppvEventConfig } = usePPVEvents();
    // Using React Query instead of Redux for configuration
    const { ppvConfigV2, operators } = useConfigurationSections();
    const { events: cmsPPVEvents = null } = ppvConfigV2 || {};
    const countryCode = useCountryCode();

    const {
        data: rawPromotions,
        isLoading: isRawLoading,
        isError: isRawError,
        error: rawError,
        isSuccess: isRawSuccess,
        isFetching: isRawFetching,
    } = useQuery({
        queryKey: ['promotions', carousel?.id],
        queryFn: () => handlePromotions(carousel),
        enabled: !!carousel?.id,
        staleTime: 1000 * 60 * 5,
        retry: 2,
        refetchOnReconnect: true,
        refetchOnWindowFocus: false,
    });

    const {
        data: modifiedPromotions,
        isLoading: isProcessing,
        isError: isProcessingError,
        error: processingError,
        isSuccess: isProcessingSuccess,
        isFetching: isProcessingFetching,
    } = useQuery({
        queryKey: [
            'promotions-processed',
            carousel?.id,
            channels?.length ?? 0,
            !!ppvEventConfig,
            !!cmsPPVEvents,
            countryCode,
        ],
        queryFn: async () => {
            const processed = await processPromotions(
                rawPromotions,
                channels,
                ppvEventConfig,
                cmsPPVEvents,
                operators,
                countryCode
            );

            savePromotionsToCache(queryClient, processed);

            return processed;
        },
        enabled: !!carousel?.id && !!rawPromotions,
        staleTime: 1000 * 60 * 5,
        retry: 2,
        refetchOnReconnect: true,
        refetchOnWindowFocus: false,
    });

    const isLoading = isRawLoading || isProcessing;
    const isError = isRawError || isProcessingError;
    const error = rawError || processingError;
    const isSuccess = isRawSuccess && isProcessingSuccess;
    const isFetching = isRawFetching || isProcessingFetching;

    return {
        modifiedPromotions,
        isLoading,
        isError,
        isSuccess,
        isFetching,
    };
};

/**
 * Hook to access shared promotions data
 * This replaces the Redux generalConfiguration.promotions
 *
 * The promotions are fetched by FeaturedSlider/HorizontalPromoSlider
 * and can be accessed by other components like PPVModal
 */
export const useSharedPromotions = () => {
    const queryClient = useQueryClient();

    // Try to get promotions from cache immediately
    const cachedPromotions = queryClient.getQueryData(PROMOTIONS_QUERY_KEY);

    return useQuery({
        queryKey: PROMOTIONS_QUERY_KEY,
        queryFn: async () => {
            // This query doesn't fetch - it just reads from cache
            // The actual fetching is done by usePromotionUtils in FeaturedSlider
            // Return cached data if available, otherwise empty array
            return cachedPromotions || [];
        },
        staleTime: Infinity, // Never goes stale
        gcTime: Infinity, // Keep in memory forever
        initialData: cachedPromotions || [], // Use cached data as initial data
    });
};
